/**
 * useResourceEditorState
 * Extracted form state management for the resource editor page.
 * Handles form values, content, cover image, validation, dirty tracking, and save.
 */

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getAuthorProDisplayName, type AdminResource } from '../types/admin-resource.type';
import {
  useAdminResource,
  useCreateResource,
  useUpdateResource,
  useDeleteResource,
  useResourceTypes,
  useResourceCategories,
} from './useAdminResources';
import { useResourceTags, useCreateResourceTag } from './useAdminResourceTags';
import { useResourceAuthors } from './useAdminResourceAuthors';
import { extractApiError, type ApiErrorDetails } from '@/core/api/types';
import type { MediaFile } from '../components/CoverImageUpload';

export type FormValues = {
  title: string;
  seoUrl: string;
  seoTitle: string;
  seoDescription: string;
  typeId: string;
  categoryIds: string[];
  tagIds: string[];
  authorIds: string[];
  authorProIds: string[];
  isFeatured: boolean;
  isFeaturedForPro: boolean;
  isBquestRecommended: boolean;
  publishedAt: string;
  affiliateLink: string;
};

const EMPTY_FORM: FormValues = {
  title: '',
  seoUrl: '',
  seoTitle: '',
  seoDescription: '',
  typeId: '',
  categoryIds: [],
  tagIds: [],
  authorIds: [],
  authorProIds: [],
  isFeatured: false,
  isFeaturedForPro: false,
  isBquestRecommended: false,
  publishedAt: '',
  affiliateLink: '',
};

// Current author ids on a loaded resource: prefer the junction links, fall back
// to the legacy scalar for resources not yet backfilled (feature 082 dual-read).
const resourceAuthorIds = (resource?: AdminResource): string[] =>
  resource?.authorLinks?.length
    ? resource.authorLinks.map((l) => l.author.id)
    : resource?.authorId
      ? [resource.authorId]
      : [];

const resourceAuthorProIds = (resource?: AdminResource): string[] =>
  resource?.authorProLinks?.length
    ? resource.authorProLinks.map((l) => l.authorPro.id)
    : resource?.authorProId
      ? [resource.authorProId]
      : [];

/** Order-insensitive set equality for deduped id arrays. */
const sameIdSet = (a: string[], b: string[]): boolean =>
  a.length === b.length && a.every((id) => b.includes(id));

export function useResourceEditorState() {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const isEditMode = !!id;

  const { data: resource, isLoading: isLoadingResource } = useAdminResource(id || '');
  const createMutation = useCreateResource();
  const updateMutation = useUpdateResource();
  const deleteMutation = useDeleteResource();
  const { data: typeOptions = [] } = useResourceTypes();
  const { data: categoryOptions = [] } = useResourceCategories();
  const { data: tagOptions = [] } = useResourceTags({ isActive: true });
  const { data: authorOptions = [] } = useResourceAuthors();
  const createTagMutation = useCreateResourceTag();

  const [formValues, setFormValues] = useState<FormValues>(EMPTY_FORM);
  const [content, setContent] = useState('');
  const [coverImage, setCoverImage] = useState<MediaFile | null>(null);
  const [videoFile, setVideoFile] = useState<MediaFile | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isDirty, setIsDirty] = useState(false);
  const [saveError, setSaveError] = useState<ApiErrorDetails | null>(null);

  const hasInitialized = useRef(false);

  useEffect(() => {
    if (!resource || !isEditMode) return;
    if (hasInitialized.current && isDirty) return;

    hasInitialized.current = true;
    setFormValues({
      title: resource.title || '',
      seoUrl: resource.seoUrl || '',
      seoTitle: resource.seoTitle || '',
      seoDescription: resource.seoDescription || '',
      typeId: resource.typeId || '',
      categoryIds: resource.categories?.map((c) => c.category.id) || [],
      tagIds: resource.tags?.map((t) => t.tag.id) || [],
      authorIds: resourceAuthorIds(resource),
      authorProIds: resourceAuthorProIds(resource),
      isFeatured: resource.isFeatured || false,
      isFeaturedForPro: resource.isFeaturedForPro || false,
      isBquestRecommended: resource.isBquestRecommended || false,
      publishedAt: resource.publishedAt?.split('T')[0] || '',
      affiliateLink: resource.affiliateLink || '',
    });
    setContent(resource.content || '');
    const primaryImage = (resource.files || []).find(
      (f) => f.fileType === 'IMAGE' && f.isPrimary,
    );
    const firstImage = primaryImage || (resource.files || []).find((f) => f.fileType === 'IMAGE');
    if (firstImage) {
      setCoverImage({
        id: firstImage.id,
        fileName: firstImage.fileName,
        fileUrl: firstImage.fileUrl,
        mimeType: firstImage.mimeType,
        fileSize: firstImage.fileSize,
      });
    }
    const firstVideo = (resource.files || []).find((f) => f.fileType === 'VIDEO');
    if (firstVideo) {
      setVideoFile({
        id: firstVideo.id,
        fileName: firstVideo.fileName,
        fileUrl: firstVideo.fileUrl,
        mimeType: firstVideo.mimeType,
        fileSize: firstVideo.fileSize,
      });
    } else {
      setVideoFile(null);
    }
  }, [resource, isEditMode, isDirty]);

  const handleFieldChange = useCallback((field: string, value: unknown) => {
    setFormValues((prev) => ({ ...prev, [field]: value }));
    setIsDirty(true);
    setSaveError(null);
    setErrors((prev) => {
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }, []);

  const handleContentChange = useCallback((html: string) => {
    setContent(html);
    setIsDirty(true);
    setSaveError(null);
    setErrors((prev) => {
      const next = { ...prev };
      delete next.content;
      return next;
    });
  }, []);

  const handleCoverImageChange = useCallback((file: MediaFile | null) => {
    setCoverImage(file);
    setIsDirty(true);
  }, []);

  const handleVideoFileChange = useCallback((file: MediaFile | null) => {
    setVideoFile(file);
    setIsDirty(true);
  }, []);

  const isContentEmpty = (html: string) => !html.replace(/<[^>]*>/g, '').trim();

  const handleSave = async () => {
    const newErrors: Record<string, string> = {};
    if (!formValues.title.trim()) newErrors.title = 'Title is required';
    if (isContentEmpty(content)) newErrors.content = 'Content cannot be empty';
    if (!formValues.typeId) newErrors.typeId = 'Type is required';
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    // Only send an author-type array when it actually changed. Sending it always
    // makes the backend deleteMany+recreate the links on every save (resetting
    // their createdAt and reordering the display) even for unrelated edits.
    // On create there is no baseline, so always send.
    const { authorIds, authorProIds, ...restForm } = formValues;
    const sendAuthorIds = !isEditMode || !sameIdSet(authorIds, resourceAuthorIds(resource));
    const sendAuthorProIds = !isEditMode || !sameIdSet(authorProIds, resourceAuthorProIds(resource));

    // Only carry the uploaded video for Video-type resources so saving any other
    // type never touches VIDEO files. For a Video resource, send null to clear.
    const isVideoType = typeOptions.find((t) => t.id === formValues.typeId)?.name === 'Video';

    const payload = {
      ...restForm,
      content,
      coverImage: coverImage
        ? { fileUrl: coverImage.fileUrl, fileName: coverImage.fileName, mimeType: coverImage.mimeType, fileSize: coverImage.fileSize }
        : null,
      ...(isVideoType && {
        videoFile: videoFile
          ? { fileUrl: videoFile.fileUrl, fileName: videoFile.fileName, mimeType: videoFile.mimeType, fileSize: videoFile.fileSize }
          : null,
      }),
      publishedAt: formValues.publishedAt
        ? new Date(formValues.publishedAt).toISOString()
        : undefined,
      affiliateLink: formValues.affiliateLink || undefined,
      ...(sendAuthorIds && { authorIds }),
      ...(sendAuthorProIds && { authorProIds }),
    };

    try {
      setSaveError(null);
      if (isEditMode && id) {
        await updateMutation.mutateAsync({ id, payload });
      } else {
        await createMutation.mutateAsync(payload);
      }
      setIsDirty(false);
      navigate('/admin/resources');
    } catch (error) {
      const details = extractApiError(error);
      setSaveError(details);
      console.error('Failed to save resource:', error);
    }
  };

  const handleTogglePublish = async () => {
    if (!isEditMode || !id) return;
    const newPublishedAt = resource?.publishedAt ? null : new Date().toISOString();
    try {
      setSaveError(null);
      await updateMutation.mutateAsync({
        id,
        payload: { publishedAt: newPublishedAt },
      });
    } catch (error) {
      const details = extractApiError(error);
      setSaveError(details);
      console.error('Failed to toggle publish:', error);
    }
  };

  const handleDelete = async () => {
    if (!isEditMode || !id) return;
    const confirmed = window.confirm(
      'Are you sure you want to delete this resource? This action cannot be undone.',
    );
    if (!confirmed) return;
    try {
      setSaveError(null);
      await deleteMutation.mutateAsync(id);
      navigate('/admin/resources');
    } catch (error) {
      const details = extractApiError(error);
      setSaveError(details);
      console.error('Failed to delete resource:', error);
    }
  };

  const handleCreateTag = useCallback(
    async (name: string) => {
      const result = await createTagMutation.mutateAsync({ name });
      return { id: result.id, name: result.name };
    },
    [createTagMutation],
  );

  const handleBack = () => {
    if (isDirty) {
      const leave = window.confirm('You have unsaved changes. Leave this page?');
      if (!leave) return;
    }
    setIsDirty(false);
    navigate('/admin/resources');
  };

  const isDirtyRef = useRef(isDirty);
  isDirtyRef.current = isDirty;

  useEffect(() => {
    const beforeUnload = (e: BeforeUnloadEvent) => {
      if (isDirtyRef.current) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    const onPopState = () => {
      if (isDirtyRef.current) {
        const leave = window.confirm('You have unsaved changes. Leave this page?');
        if (!leave) window.history.pushState(null, '', window.location.href);
      }
    };
    window.history.pushState(null, '', window.location.href);
    window.addEventListener('beforeunload', beforeUnload);
    window.addEventListener('popstate', onPopState);
    return () => {
      window.removeEventListener('beforeunload', beforeUnload);
      window.removeEventListener('popstate', onPopState);
    };
  }, []);

  const isSaving = createMutation.isPending || updateMutation.isPending;
  const isPublished = !!resource?.publishedAt;

  // id -> display name for the selected SP/FA authors, so the editor's chips
  // render on load without a lookup. Covers the junction links and the legacy
  // single authorPro (dual-read).
  const initialAuthorProNames = useMemo(() => {
    const map: Record<string, string> = {};
    resource?.authorProLinks?.forEach((link) => {
      map[link.authorPro.id] = getAuthorProDisplayName(link.authorPro);
    });
    if (resource?.authorPro && resource.authorProId && !map[resource.authorProId]) {
      map[resource.authorProId] = getAuthorProDisplayName(resource.authorPro);
    }
    return map;
  }, [resource?.authorProLinks, resource?.authorPro, resource?.authorProId]);

  const dismissSaveError = useCallback(() => setSaveError(null), []);

  return {
    id,
    isEditMode,
    isLoadingResource,
    isSaving,
    isPublished,
    resource,
    formValues,
    content,
    coverImage,
    videoFile,
    selectedTypeName: typeOptions.find((t) => t.id === formValues.typeId)?.name,
    errors,
    isDirty,
    saveError,
    typeOptions,
    categoryOptions: categoryOptions.map((c) => ({ id: c.id, name: c.name })),
    tagOptions: tagOptions.map((t) => ({ id: t.id, name: t.name, isActive: t.isActive })),
    authorOptions: authorOptions.map((a) => ({ id: a.id, name: a.name })),
    initialAuthorProNames,
    handleFieldChange,
    handleContentChange,
    handleCoverImageChange,
    handleVideoFileChange,
    handleSave,
    handleBack,
    handleTogglePublish,
    handleDelete,
    handleCreateTag,
    dismissSaveError,
  };
}
